import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import LogoMarquee from '../LogoMarquee'
import Parallax from '../Parallax'

const Footer: React.FC = () => {
  const { pathname } = useLocation()
  // P3.7: hash anchors only exist on Home — off-home routes need `/#section`
  const resolve = (href: string): string =>
    href.startsWith('#') && pathname !== '/' ? `/${href}` : href
  return (
    <footer className="bg-ink relative overflow-hidden">
      {/* Dark Lewes Beach panorama + heavy overlay — 1.4-L */}
      {/* Real Lewes Beach photo (Wikimedia CC BY 2.0, see IMAGE_MANIFEST.md); final = client drone still */}
      <Parallax factor={0.08} className="absolute -inset-y-[12%] inset-x-0">
        <img
          src="/media/footer-lewes-beach.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </Parallax>
      <div className="absolute inset-0 bg-primary-ink/85" aria-hidden="true" />
      
      <div className="relative max-w-1200 mx-auto px-6">
        {/* Repeated nav row (thin top border) */}
        <div className="border-t border-neutral-slate/30 pt-6 pb-8">
          <nav className="flex flex-wrap justify-center gap-6 md:gap-10" aria-label="Footer Navigation">
            <a href={resolve('#listings')} className="inline-flex min-h-11 min-w-11 items-center justify-center text-xs md:text-sm font-display uppercase tracking-wider text-white/80 hover:text-gold transition-colors">
              Listings
            </a>
            <a href={resolve('#home-tours')} className="inline-flex min-h-11 min-w-11 items-center justify-center text-xs md:text-sm font-display uppercase tracking-wider text-white/80 hover:text-gold transition-colors">
              Home Tours
            </a>
            <a href="/areas" className="inline-flex min-h-11 min-w-11 items-center justify-center text-xs md:text-sm font-display uppercase tracking-wider text-white/80 hover:text-gold transition-colors">
              Beach Towns
            </a>
            <a href="/sell" className="inline-flex min-h-11 min-w-11 items-center justify-center text-xs md:text-sm font-display uppercase tracking-wider text-white/80 hover:text-gold transition-colors">
              Sellers
            </a>
            <a href="/buy" className="inline-flex min-h-11 min-w-11 items-center justify-center text-xs md:text-sm font-display uppercase tracking-wider text-white/80 hover:text-gold transition-colors">
              Buyers
            </a>
            <a href="/faq" className="inline-flex min-h-11 min-w-11 items-center justify-center text-xs md:text-sm font-display uppercase tracking-wider text-white/80 hover:text-gold transition-colors">
              FAQ
            </a>
            <a href="/about" className="inline-flex min-h-11 min-w-11 items-center justify-center text-xs md:text-sm font-display uppercase tracking-wider text-white/80 hover:text-gold transition-colors">
              Our Story
            </a>
            <a href={resolve('#contact')} className="inline-flex min-h-11 min-w-11 items-center justify-center text-xs md:text-sm font-display uppercase tracking-wider text-white/80 hover:text-gold transition-colors">
              Contact Us
            </a>
          </nav>
        </div>

        {/* Copyright/licensing/privacy row */}
        <div className="border-t border-neutral-slate/30 py-6">
          <p className="text-xs md:text-sm font-sans text-white/70 leading-relaxed text-center md:text-left">
            Shawn McDonnell, Associate Broker & Merritt Cooch, REALTOR® · Jack Lingo, REALTOR® · 
            1240 Kings Highway, Lewes, DE 19958 · Office 302-645-2207 · Licensed in Delaware
          </p>
        </div>

        {/* Row of association logos — marquee on mobile, static on desktop (A.1) */}
        <div className="border-t border-neutral-slate/30 py-6">
          <LogoMarquee />
        </div>

        {/* Bright MLS IDX disclaimer — VERBATIM per A.6 (required wherever live listing data renders) */}
        <div className="border-t border-neutral-slate/30 py-6">
          <p className="text-[10px] font-sans text-white/60 leading-relaxed text-center max-w-4xl mx-auto">
            The data relating to real estate for sale on this website appears in part through the BRIGHT Internet Data Exchange program, a voluntary cooperative exchange of property listing data between licensed real estate brokerage firms in which Jack Lingo REALTOR® participates, and is provided by BRIGHT through a licensing agreement. The information provided by this website is for the personal, non-commercial use of consumers and may not be used for any purpose other than to identify prospective properties consumers may be interested in purchasing. Some properties which appear for sale on this website may no longer be available.
          </p>
          <p className="mt-2 text-[10px] font-sans text-white/60 text-center">
            ©2026 BRIGHT MLS, All Rights Reserved. Information deemed reliable but not guaranteed.
          </p>
        </div>

        {/* Bottom row: copyright + legal links (A.6) */}
        <div className="border-t border-neutral-slate/30 py-4 flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 text-center">
          <p className="text-[10px] font-sans text-white/50">
            &copy; {new Date().getFullYear()} Merritt &amp; Shawn | Coastal Delaware Real Estate. All rights reserved.
          </p>
          <span className="hidden md:inline text-white/30" aria-hidden="true">·</span>
          <Link to="/sitemap" className="inline-flex min-h-11 items-center px-2 text-[10px] font-sans text-white/50 hover:text-gold transition-colors">
            Sitemap
          </Link>
          <span className="hidden md:inline text-white/30" aria-hidden="true">·</span>
          <Link to="/privacy" className="inline-flex min-h-11 items-center px-2 text-[10px] font-sans text-white/50 hover:text-gold transition-colors">
            Privacy Policy
          </Link>
          <span className="hidden md:inline text-white/30" aria-hidden="true">·</span>
          <Link to="/accessibility" className="inline-flex min-h-11 items-center px-2 text-[10px] font-sans text-white/50 hover:text-gold transition-colors">
            Accessibility
          </Link>
          <span className="hidden md:inline text-white/30" aria-hidden="true">·</span>
          <Link to="/privacy" className="inline-flex min-h-11 items-center px-2 text-[10px] font-sans text-white/50 hover:text-gold transition-colors">
            Do Not Sell My Personal Information
          </Link>
        </div>
      </div>
    </footer>
  )
}

export default Footer
